import { notifications } from "@mantine/notifications";
import React, { useState } from "react";
import { X } from "lucide-react";
import { BannerAdminRequestDTO } from "Types/Admin/AdminBannerType";

interface BannerFormProps {
  initialValues: BannerAdminRequestDTO;
  onSubmit: (
    data: BannerAdminRequestDTO,
    imageFile?: File,
    mobileImageFile?: File,
  ) => void;
  onCancel: () => void;
  mode: "add" | "edit" | "view";
  existingImage?: string;
  existingMobileImage?: string;
  onDeleteDesktopImage?: () => Promise<void>;
  onDeleteMobileImage?: () => Promise<void>;
}

export const BannerForm: React.FC<BannerFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  mode,
  existingImage,
  existingMobileImage,
  onDeleteDesktopImage,
  onDeleteMobileImage,
}) => {
  const [formData, setFormData] =
    useState<BannerAdminRequestDTO>(initialValues);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [mobileImageFile, setMobileImageFile] = useState<File | null>(null);
  const [mobileImagePreview, setMobileImagePreview] = useState<string | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingDesktop, setIsDeletingDesktop] = useState(false);
  const [isDeletingMobile, setIsDeletingMobile] = useState(false);

  const handleDeleteDesktopImage = async () => {
    // If a new file is selected but not yet saved, just clear it locally.
    if (imageFile) {
      setImageFile(null);
      setImagePreview(existingImage ?? null);
      return;
    }

    if (!onDeleteDesktopImage) return;

    try {
      setIsDeletingDesktop(true);
      await onDeleteDesktopImage();
      setImagePreview(null);
    } catch (error: any) {
      notifications.show({
        message:
          error?.data?.message ||
          "Failed to delete desktop image. Please try again.",
        color: "red",
      });
    } finally {
      setIsDeletingDesktop(false);
    }
  };

  const handleDeleteMobileImage = async () => {
    if (mobileImageFile) {
      setMobileImageFile(null);
      setMobileImagePreview(existingMobileImage ?? null);
      return;
    }

    if (!onDeleteMobileImage) return;

    try {
      setIsDeletingMobile(true);
      await onDeleteMobileImage();
      setMobileImagePreview(null);
    } catch (error: any) {
      notifications.show({
        message:
          error?.data?.message ||
          "Failed to delete mobile image. Please try again.",
        color: "red",
      });
    } finally {
      setIsDeletingMobile(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    let maxLengths: Record<string, number> = {
      title: 100,
      buttonText: 50,
      description: 200,
      link: 500,
    };
    if (maxLengths[name] && value.length > maxLengths[name]) {
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateFileSize = (file: File): boolean => {
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return false;
    }
    return true;
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!validateFileSize(file)) {
        e.target.value = "";
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMobileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!validateFileSize(file)) {
        e.target.value = "";
        return;
      }
      setMobileImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMobileImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (formData.link && !formData.buttonText?.trim()) {
      return;
    }

    setIsSubmitting(true);

    Promise.resolve(
      onSubmit(formData, imageFile || undefined, mobileImageFile || undefined),
    ).finally(() => {
      setIsSubmitting(false);
    });
  };

  const isViewMode = mode === "view";
  const readOnlyProps = isViewMode ? { readOnly: true } : {};

  React.useEffect(() => {
    if (existingImage && mode === "edit") {
      setImagePreview(existingImage);
    }
  }, [existingImage, mode]);

  React.useEffect(() => {
    if (existingMobileImage && mode === "edit") {
      setMobileImagePreview(existingMobileImage);
    }
  }, [existingMobileImage, mode]);

  return (
    <form onSubmit={handleSubmit} className="font-body tracking-wider">
      <div className="bg-white rounded-md shadow-md my-6 flex flex-col">
        <div className="px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Title*
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                  required
                  maxLength={100}
                  {...readOnlyProps}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0 min-h-[100px]"
                  maxLength={200}
                  {...readOnlyProps}
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.description.length}/200 characters
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Button Text
                </label>
                <input
                  type="text"
                  name="buttonText"
                  value={formData.buttonText}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                  maxLength={50}
                  {...readOnlyProps}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Link URL*
                </label>
                <input
                  type="url"
                  name="link"
                  value={formData.link}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                  required
                  maxLength={500}
                  placeholder="https://example.com"
                  {...readOnlyProps}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Desktop Image 
                </label>
                {isViewMode ? (
                  existingImage ? (
                    <div className="mt-2 relative inline-block group">
                      <img
                        src={existingImage}
                        alt="Desktop Banner preview"
                        className="max-h-40 object-contain border rounded"
                        width="auto"
                        height="auto"
                      />
                      {mode !== "view" && (
                        <button
                          type="button"
                          onClick={handleDeleteDesktopImage}
                          disabled={isDeletingDesktop}
                          className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-500 hover:text-red-700 shadow opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                          title="Delete desktop image"
                        >
                          {isDeletingDesktop ? (
                            <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <X size={16} />
                          )}
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400">No image</span>
                  )
                ) : (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                     required={false}
                    />
                    {imagePreview && (
                      <div className="mt-2 relative inline-block group">
                        <img
                          src={imagePreview}
                          alt="Desktop Banner preview"
                          className="max-h-40 object-contain border rounded"
                          width="auto"
                          height="auto"
                        />
                        <button
                          type="button"
                          onClick={handleDeleteDesktopImage}
                          disabled={isDeletingDesktop}
                          className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-500 hover:text-red-700 shadow opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                          title="Delete desktop image"
                        >
                          {isDeletingDesktop ? (
                            <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <X size={16} />
                          )}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Mobile Image 
                </label>
                {isViewMode ? (
                  existingMobileImage ? (
                    <div className="mt-2 relative inline-block group">
                      <img
                        src={existingMobileImage}
                        alt="Mobile Banner preview"
                        className="max-h-40 object-contain border rounded"
                      />
                      {mode !== "view" && (
                        <button
                          type="button"
                          onClick={handleDeleteMobileImage}
                          disabled={isDeletingMobile}
                          className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-500 hover:text-red-700 shadow opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                          title="Delete mobile image"
                        >
                          {isDeletingMobile ? (
                            <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <X size={16} />
                          )}
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400">No image</span>
                  )
                ) : (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleMobileImageChange}
                      className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                      required={false}
                    />
                    {mobileImagePreview && (
                      <div className="mt-2 relative inline-block group">
                        <img
                          src={mobileImagePreview}
                          alt="Mobile Banner preview"
                          className="max-h-40 object-contain border rounded"
                        />
                        <button
                          type="button"
                          onClick={handleDeleteMobileImage}
                          disabled={isDeletingMobile}
                          className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-500 hover:text-red-700 shadow opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                          title="Delete mobile image"
                        >
                          {isDeletingMobile ? (
                            <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <X size={16} />
                          )}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {mode !== "view" && (
          <div className="bg-white border-t px-6 py-4 rounded-b-md">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white text-blue-600 hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-2 text-sm font-medium rounded-md text-white
          ${
            isSubmitting
              ? "bg-blue-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }
        `}
              >
                {isSubmitting
                  ? mode === "add"
                    ? "Adding..."
                    : "Saving..."
                  : mode === "add"
                    ? "Add Banner"
                    : "Save Changes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </form>
  );
};
